import { useState } from 'react'
import { Copy, Check, Eye, EyeOff, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { copyToClipboard } from '@/utils/clipboard'
import RevealWarningDialog from '@/components/dialogs/RevealWarningDialog'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog'

interface PrivateKeyRevealProps {
  privateKey: string
  className?: string
}

const PrivateKeyReveal = ({ privateKey, className = '' }: PrivateKeyRevealProps) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const [isRevealed, setIsRevealed] = useState(false)
  const [showWarningDialog, setShowWarningDialog] = useState(false)
  const [showCopyConfirm, setShowCopyConfirm] = useState(false)

  const confirmCopy = async () => {
    const success = await copyToClipboard(privateKey)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const toggleReveal = () => {
    if (isRevealed) {
      setIsRevealed(false)
      return
    }
    setShowWarningDialog(true)
  }

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-text-secondary font-medium">
          {t('recover.key_label')}
        </p>
        <div className="flex gap-2">
          <button
            onClick={toggleReveal}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card hover:bg-card/80 transition-colors text-text-secondary text-sm"
          >
            {isRevealed ? (
              <>
                <EyeOff className="w-4 h-4" />
                <span className="hidden sm:inline">{t('common.hide')}</span>
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">{t('common.reveal')}</span>
              </>
            )}
          </button>
          <button
            onClick={() => setShowCopyConfirm(true)}
            disabled={!isRevealed}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card hover:bg-card/80 transition-colors text-text-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-accent" />
                <span className="hidden sm:inline text-accent">{t('common.copied')}</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span className="hidden sm:inline">{t('common.copy')}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Key with blur overlay */}
      <div className="relative">
        {!isRevealed && (
          <div className="absolute inset-0 backdrop-blur-md bg-card/30 rounded-xl z-10 flex items-center justify-center">
            <button
              onClick={toggleReveal}
              className="flex flex-col items-center gap-2 text-text-main hover:text-primary transition-colors"
            >
              <Eye className="w-8 h-8" />
              <p className="text-sm font-medium">{t('common.click_reveal')}</p>
            </button>
          </div>
        )}

        <div className="bg-card rounded-xl p-4 border border-red-500/30">
          <p className="text-text-main font-mono text-sm break-all select-none">
            {isRevealed ? privateKey : '•'.repeat(privateKey.length || 64)}
          </p>
        </div>
      </div>

      {/* Warning message */}
      <div className="flex gap-3 p-4 bg-card rounded-xl border border-red-500/30">
        <div className="shrink-0">
          <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
            <TriangleAlert className="h-4 w-4 text-red-500" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-text-main">
            {t('recover.warning_title')}
          </p>
          <p className="text-sm text-text-secondary leading-relaxed">
            {t('recover.warning_desc')}
          </p>
        </div>
      </div>

      <RevealWarningDialog
        open={showWarningDialog}
        onClose={() => setShowWarningDialog(false)}
        onConfirm={() => setIsRevealed(true)}
        variant="privateKey"
      />

      <ConfirmDialog
        open={showCopyConfirm}
        onClose={() => setShowCopyConfirm(false)}
        onConfirm={confirmCopy}
        variant="danger"
        title={t('recover.copy_confirm_title')}
        description={t('recover.copy_key_confirm_desc')}
        confirmLabel={t('recover.copy_confirm_button')}
      />
    </div>
  )
}

export default PrivateKeyReveal
